import { Controller } from '@hotwired/stimulus';
import { open } from '../lib/maplibre';
import MapController from './map_controller';

// A door on a form (docs/ADDRESS.md § The pin). CEP, or a pick of "Or search",
// names the city; this zooms there. A click, a drag of the marker or the
// "Pin here" button (the keyboard's road) writes geom: the precise pin, not
// the municipality's. Needs maplibre-gl in the host, like `map`.
export default class extends Controller {
  static targets = ['wrap', 'canvas', 'here', 'lng', 'lat', 'said'];
  static values = {
    geom: { type: Array, default: [] },
    zoom: { type: Number, default: 14 },
    // Empty: the quiet basemap for whichever theme the page is wearing, as `map`.
    style: String,
    // The marker's classes (tokens): the view says them, so Tailwind scans them.
    marker: String,
    // What a screen reader hears once the pin is set.
    set: String,
  };

  map = null;
  marker = null;
  gl = null;

  connect() {
    const at = this.pair(this.geomValue);
    if (at) this.show(at, { write: this.hasLngTarget && this.lngTarget.value !== '' });
  }

  disconnect() {
    this.theme?.disconnect();
    this.map?.remove();
    this.map = null;
    this.marker = null;
  }

  // cep:found — the city's gazetteer pin, not BrasilAPI's centroid.
  city({ detail }) {
    const at = this.pair(detail?.geom);
    if (at) this.show(at, { write: false });
  }

  async show(at, { write } = {}) {
    this.wrapTarget.hidden = false;
    if (write) this.sync(at);
    // MapLibre is its own chunk, fetched by the first pin that needs a map (docs/MAPS.md § The controller).
    this.gl ||= await import('maplibre-gl');
    if (!this.element.isConnected) return;

    if (this.map) {
      this.map.resize();
      this.map.jumpTo({ center: at, zoom: this.zoomValue });
      this.drop(at);
      return;
    }

    this.map = open(this.gl, {
      container: this.canvasTarget,
      style: this.tiles(),
      center: at,
      zoom: this.zoomValue,
    });
    // No WebGL2: the CEP still names the city and the form still saves.
    if (!this.map) {
      this.wrapTarget.hidden = true;
      return;
    }
    this.hereTarget.disabled = false;
    this.map.addControl(new this.gl.NavigationControl({ showCompass: false }), 'top-right');
    this.map.on('load', () => this.drop(at));
    this.map.on('click', (e) => this.venue([e.lngLat.lng, e.lngLat.lat]));
    // The theme switch flips data-theme on <html>; the tiles follow, the marker is DOM.
    this.theme = new MutationObserver(() => this.map?.setStyle(this.tiles()));
    this.theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  }

  tiles() {
    if (this.styleValue) return this.styleValue;
    const scheme = getComputedStyle(document.documentElement).colorScheme;
    return MapController.TILES[scheme === 'light' ? 'light' : 'dark'];
  }

  // "Pin here": the keyboard pans the map, the button sets the door at its centre.
  // Disabled until show() has built the map.
  center() {
    const { lng, lat } = this.map?.getCenter() ?? {};
    if (lng != null) this.venue([lng, lat]);
  }

  venue(at) {
    this.drop(at);
    this.sync(at);
    this.say();
  }

  // A polite live region: cleared first, so a second pin is heard again.
  say() {
    if (!this.hasSaidTarget) return;
    this.saidTarget.textContent = '';
    requestAnimationFrame(() => (this.saidTarget.textContent = this.setValue));
  }

  drop(at) {
    if (!this.map) return;

    if (this.marker) {
      this.marker.setLngLat(at);
      return;
    }
    const element = document.createElement('div');
    element.className = this.markerValue;
    this.marker = new this.gl.Marker({ element, draggable: true }).setLngLat(at).addTo(this.map);
    this.marker.on('dragend', () => {
      const { lng, lat } = this.marker.getLngLat();
      this.sync([lng, lat]);
      this.say();
    });
  }

  sync(at) {
    if (!this.hasLngTarget || !this.hasLatTarget) return;

    this.lngTarget.disabled = false;
    this.latTarget.disabled = false;
    this.lngTarget.value = at[0];
    this.latTarget.value = at[1];
  }

  pair(value) {
    const [lng, lat] = Array.isArray(value) ? value : [];
    if (lng == null || lat == null || lng === '' || lat === '') return null;
    const at = [Number(lng), Number(lat)];
    return at.every(Number.isFinite) ? at : null;
  }
}
